import { createEffect, createMemo, createSignal, on, Show, untrack, type VoidComponent } from "solid-js";
import type { FormVersion } from "../../../state/formVersion";
import Popover from "../../../components/popover/Popover";
import { useTranslation } from "../../../state/i18n";
import Text from "../../../components/text/Text";
import { TextAndIconButton, TextButton } from "../../../components/button/Button";
import ApiService from "../../../services/ApiService";
import {
  formScopeToString,
  mapColumnIdsToDataNames,
  mapExportFieldToScopedFields,
  mapMetaIcons,
  mapScopedFieldsToExportField,
} from "../../../utils/mappers/exportFields";
import type { ExportField, ExportFieldWithFieldType } from "../../../types/ExportField";
import { useUserState } from "../../../state/user";
import { SingleSelectWithSearchInput } from "../../../components/menu/SingleSelectWithSearch";
import { SingleSelect, SingleSelectInput } from "../../../components/menu/Select";
import { Checkbox } from "../../../components/checkbox/Checkbox";
import type { Filter } from "./filter";
import SubmissionId from "../../../state/SubmissionId";
import { isEmpty } from "lodash-es";
import { Scope } from "../../../types";
import type { Item } from "../../../fields/list/FieldList";
import FieldList from "../../../fields/list/FieldList";
import { fields } from "../../../fields/fields";
import { getIconCategoryColor } from "../../../utils/iconColorUtil";
import SearchBar from "../../../components/searchBar/SearchBar";
import { Icon } from "../../../components/icon/Icon";
import { Translation } from "../../../utils/Translation";
import type { SelectOption } from "../../../types/Menu";
import { useCustomerState } from "../../../state/customer";
import { MoreAppError } from "../../../MoreAppError";
import logger from "../../../utils/logger";
import { useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import { useTable } from "./TableProvider";
import { TIMEZONE_OPTIONS, toExcelDatePattern } from "../../../utils/timeUtil";
import { keyFromSelector } from "i18next";
import { MAX_SUBMISSIONS_ALL_FILES_EXPORT } from "../../../constants.ts";

export type ExportSubmissionsButtonProps = {
  formVersion: FormVersion;
};

let exportFields: ExportField[] = [];
let currentFields: Record<string, ExportFieldWithFieldType[]> = {};

type ViewType = "export" | "custom" | "final";
type ColumnType = "all" | "visible" | "custom";
type RowType = "all" | "selected" | "filtered";

type ItemWithExportField = Item & { field: ExportField };

const defaultSelectedDataNames = ["meta.userId", "meta.serialNumber", "meta.registrationDate"];

const ExportSubmissionsButton: VoidComponent<ExportSubmissionsButtonProps> = (props) => {
  const { t, currentLanguage } = useTranslation();
  const { replace } = useModalManager();
  const tableState = useTable();
  const [open, setOpen] = createSignal<boolean>(false);
  const [loading, setLoading] = createSignal<boolean>(false);
  const [column, setColumn] = createSignal<ColumnType>("all");
  const [row, setRow] = createSignal<RowType>("all");
  const [singleExcel, setSingleExcel] = createSignal<boolean>(false);
  const [allFiles, setAllFiles] = createSignal<boolean>(false);
  const [query, setQuery] = createSignal<string>("");
  const [isExporting, setIsExporting] = createSignal<boolean>(false);

  const [view, setView] = createSignal<ViewType>("export");

  const user = useUserState();
  const customer = useCustomerState();
  const [timezone, setTimezone] = createSignal(user().timezone);

  const selectedRows = createMemo(() => {
    const rowSelection = tableState.rowSelection();
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => SubmissionId.from(key));
  });

  const hasActiveFilters = createMemo(() => !isEmpty(tableState.filterStore()?.all()));
  const hasSelectedSubmissions = createMemo(() => !isEmpty(selectedRows()));

  const exportedSubmissionCount = createMemo(() =>
    row() === "selected" ? selectedRows().length : tableState.totalCount(),
  );
  const exceedsFileExportLimit = createMemo(() => exportedSubmissionCount() > MAX_SUBMISSIONS_ALL_FILES_EXPORT);

  // Switching row type can push the export over the limit while the popover is open
  createEffect(() => {
    if (exceedsFileExportLimit()) {
      setAllFiles(false);
    }
  });

  const subformOptions = createMemo<SelectOption<string>[]>(() => [
    { variant: "text", value: formScopeToString(Scope.ROOT), label: "Main form" },
    ...props.formVersion.subforms.map<SelectOption<string>>((subform) => ({
      variant: "text",
      value: subform.uid.toString(),
      label: subform.meta.name,
    })),
  ]);

  const [currentForm, setCurrentForm] = createSignal<string>(formScopeToString(Scope.ROOT));
  const [currentItems, setCurrentItems] = createSignal<Record<string, ItemWithExportField[]>>({});

  // Clear search when switching forms
  createEffect(
    on(
      () => currentForm(),
      () => {
        setQuery("");
      },
    ),
  );

  const allItemsSelected = createMemo(() => (currentItems()[currentForm()] ?? []).every((item) => item.selected));
  const toggleAllColumns = () => {
    const cc = currentForm();
    setCurrentItems((prev) => ({
      ...prev,
      [cc]: prev[cc].map((item: ItemWithExportField) => ({ ...item, selected: !allItemsSelected() })),
    }));
  };

  const filteredItems = createMemo(() => {
    const searchQuery = query().toLowerCase();
    return (currentItems()[currentForm()] ?? []).filter((item) => item.label.toLowerCase().includes(searchQuery));
  });

  const toggleItem = (value: boolean, id: string, currentForm: string) => {
    setCurrentItems((prev) => ({
      ...prev,
      [currentForm]: prev[currentForm].map((item: ItemWithExportField) =>
        item.id === id ? { ...item, selected: value } : item,
      ),
    }));
  };

  const reOrderItem = (fromIndex: number, toIndex: number, currentForm: string) => {
    setCurrentItems((prev) => {
      const items = [...prev[currentForm]];
      const [item] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, item);
      return { ...prev, [currentForm]: items };
    });
  };

  const mapExportFieldWithTypeToListItem = (exportFieldWithFieldType: ExportFieldWithFieldType[]) =>
    exportFieldWithFieldType.map((field, index) => {
      const { icon, iconClasses, selected } =
        field.fieldType === "unknown"
          ? {
              icon: mapMetaIcons(field.dataName),
              iconClasses: "bg-surface-subtle icon-primary",
              selected: defaultSelectedDataNames.includes(field.dataName),
            }
          : {
              icon: fields[field.fieldType].icon,
              iconClasses: getIconCategoryColor(fields[field.fieldType].category),
              selected: false,
            };
      return {
        id: field.dataName,
        label: field.name,
        icon,
        iconClasses,
        index,
        selected,
        onSelected: (value: boolean) => toggleItem(value, field.dataName, currentForm()),
        field,
      };
    });

  const handleOpenPopover = () => {
    setLoading(true);

    ApiService.getExportFields(props.formVersion.formId)
      .then((data) => {
        untrack(() => {
          exportFields = data;
          currentFields = mapExportFieldToScopedFields(data, props.formVersion);
          setCurrentItems(
            Object.entries(currentFields).reduce(
              (acc, [key, value]) => ({
                ...acc,
                [key]: mapExportFieldWithTypeToListItem(value),
              }),
              {},
            ),
          );
        });
        setColumn("all");
        if (hasSelectedSubmissions()) {
          setRow("selected");
        } else if (hasActiveFilters()) {
          setRow("filtered");
        } else {
          setRow("all");
        }
        setSingleExcel(false);
        setAllFiles(false);
        setQuery("");
        setCurrentForm(formScopeToString(Scope.ROOT));
        setView("export");
        setOpen(true);
      })
      .catch((e) => {
        if (e instanceof MoreAppError) {
          replace({
            content: () => <GenericFailureModal error={e} />,
          });
        } else {
          logger.error("Error fetching export fields", e);
        }

        setOpen(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getColumns = (): ExportField[] => {
    const currentColumn = column();
    if (currentColumn === "all") {
      return exportFields;
    } else if (currentColumn === "visible") {
      return exportFields.filter(({ dataName }) =>
        tableState.columnVisibility().map(mapColumnIdsToDataNames).includes(dataName),
      );
    } else {
      const items = currentItems();

      const keys = Object.keys(items);
      const fields = keys.reduce(
        (acc, key) => ({ [key]: items[key].filter((item) => item.selected).map(({ field }) => field), ...acc }),
        {},
      );

      return mapScopedFieldsToExportField(fields);
    }
  };

  const getFilters = (): Filter[] => (row() === "filtered" ? (tableState.filterStore()?.all() ?? []) : []);

  const getSubmissionIds = () => {
    if (row() === "selected") {
      return selectedRows();
    }

    return [];
  };

  const runExport = () => {
    setIsExporting(true);

    ApiService.requestExport(
      props.formVersion.formId,
      getColumns(),
      user().emailAddress,
      getFilters(),
      getSubmissionIds(),
      timezone(),
      toExcelDatePattern(customer.settings.dateFormat),
      allFiles(),
      singleExcel(),
      currentLanguage(),
    )
      .then(() => setView("final"))
      .catch((e) => {
        if (e instanceof MoreAppError) {
          replace({
            content: () => <GenericFailureModal error={e} />,
          });
        } else {
          logger.error("Error fetching export fields", e);
        }

        setOpen(false);
      })
      .finally(() => setIsExporting(false));
  };

  return (
    <Popover
      open={open()}
      onOpenChange={setOpen}
      variant="lg"
      Title={() => <h3 class="text-header-lg text-primary">{t(($) => $.TABLE_COLUMN_SETTINGS_EXPORT_TITLE)}</h3>}
      aria-label={t(($) => $.TABLE_COLUMN_SETTINGS_EXPORT_TITLE)}
      Trigger={(triggerProps) => (
        <TextAndIconButton
          {...triggerProps}
          icon="download"
          text={t(($) => $.TABLE_COLUMN_SETTINGS_EXPORT_TITLE)}
          variant="secondary"
          loading={loading()}
          disabled={loading()}
          onClick={() => handleOpenPopover()}
        />
      )}
      positioning={{ placement: "bottom-end", flip: ["left-start"] }}
    >
      <Show when={view() !== "final"}>
        <Text
          text={t(($) => $.EXPORT_SUBMISSIONS_DESCRIPTION)}
          class="flex border-b border-subtle px-4 pt-1 pb-4 text-base-regular text-secondary"
        />
      </Show>

      <Show when={view() === "export"}>
        <div class="flex flex-col gap-6 p-4">
          <SingleSelectWithSearchInput
            options={{ timezones: TIMEZONE_OPTIONS }}
            label={t(($) => $.EXPORT_SUBMISSIONS_TIMEZONE_LABEL)}
            width="matchTrigger"
            onSelect={(t) => {
              setTimezone(t as string);
            }}
            value={timezone()}
            info={{
              title: t(($) => $.EXPORT_SUBMISSIONS_TIMEZONE_HELP_TITLE),
              description: t(($) => $.EXPORT_SUBMISSIONS_TIMEZONE_HELP_DESCRIPTION),
              alt: t(($) => $.EXPORT_SUBMISSIONS_TIMEZONE_HELP_ALT),
            }}
          />
          <SingleSelectInput
            options={[
              { value: "all" as ColumnType, label: t(($) => $.EXPORT_SUBMISSIONS_ALL_COLUMNS_LABEL), variant: "text" },
              {
                value: "visible" as ColumnType,
                label: t(($) => $.EXPORT_SUBMISSIONS_VISIBLE_COLUMNS_LABEL),
                variant: "text",
              },
              {
                value: "custom" as ColumnType,
                label: t(($) => $.EXPORT_SUBMISSIONS_CUSTOM_COLUMNS_LABEL),
                variant: "text",
              },
            ]}
            label={t(($) => $.EXPORT_SUBMISSIONS_COLUMNS_LABEL)}
            onSelect={(column) => setColumn(column)}
            sameWidth
            value={column()}
            info={{
              title: t(($) => $.EXPORT_SUBMISSIONS_COLUMNS_HELP_TITLE),
              description: t(($) => $.EXPORT_SUBMISSIONS_COLUMNS_HELP_DESCRIPTION),
              alt: t(($) => $.EXPORT_SUBMISSIONS_COLUMNS_HELP_ALT),
            }}
          />
          <SingleSelectInput
            options={[
              { value: "all" as RowType, label: t(($) => $.EXPORT_SUBMISSIONS_ALL_ROWS_LABEL), variant: "text" },
              {
                value: "selected" as RowType,
                label: t(($) => $.EXPORT_SUBMISSIONS_SELECTED_ROWS_LABEL),
                variant: "text",
                disabled: !hasSelectedSubmissions(),
              },
              {
                value: "filtered" as RowType,
                label: t(($) => $.EXPORT_SUBMISSIONS_FILTERED_ROWS_LABEL),
                variant: "text",
                disabled: !hasActiveFilters(),
              },
            ]}
            label={t(($) => $.EXPORT_SUBMISSIONS_SUBMISSIONS_LABEL)}
            onSelect={(row) => setRow(row)}
            sameWidth
            value={row()}
            info={{
              title: t(($) => $.EXPORT_SUBMISSIONS_SUBMISSIONS_HELP_TITLE),
              description: t(($) => $.EXPORT_SUBMISSIONS_SUBMISSIONS_HELP_DESCRIPTION),
              alt: t(($) => $.EXPORT_SUBMISSIONS_SUBMISSIONS_HELP_ALT),
            }}
          />
          <Checkbox
            label={t(($) => $.EXPORT_SUBMISSIONS_EXPORT_TO_SINGLE_LABEL)}
            checked={singleExcel()}
            setChecked={(checked) => setSingleExcel(checked === true)}
            block
            info={{
              title: t(($) => $.EXPORT_SUBMISSIONS_EXPORT_TO_SINGLE_HELP_TITLE),
              description: t(($) => $.EXPORT_SUBMISSIONS_EXPORT_TO_SINGLE_HELP_DESCRIPTION),
              alt: t(($) => $.EXPORT_SUBMISSIONS_EXPORT_TO_SINGLE_HELP_ALT),
            }}
          />
          <Checkbox
            label={t(($) => $.EXPORT_SUBMISSIONS_EXPORT_INCLUDE_ALL_FILES_LABEL)}
            checked={allFiles()}
            setChecked={(checked) => setAllFiles(checked === true)}
            block
            disabled={exceedsFileExportLimit()}
            info={{
              title: t(($) => $.EXPORT_SUBMISSIONS_EXPORT_INCLUDE_ALL_FILES_HELP_TITLE),
              description: t(($) => $.EXPORT_SUBMISSIONS_EXPORT_INCLUDE_ALL_FILES_HELP_DESCRIPTION),
              alt: t(($) => $.EXPORT_SUBMISSIONS_EXPORT_INCLUDE_ALL_FILES_HELP_ALT),
            }}
          />
        </div>
      </Show>

      <Show when={view() === "custom"}>
        <div class="flex flex-col px-2 pt-4">
          <div class="grid grid-cols-3 gap-x-2 px-2 pb-2">
            <div
              classList={{
                "col-span-2": subformOptions().length > 1,
                "col-span-full": !(subformOptions().length > 1),
              }}
            >
              <SearchBar
                aria-expanded="true"
                role="combobox"
                placeholder={t(($) => $.MENU_SEARCH_PLACEHOLDER_DEFAULT)}
                query={query()}
                onQueryChange={setQuery}
              />
            </div>
            <Show when={subformOptions().length > 1}>
              <SingleSelect
                ariaLabel={t(($) => $.MENU_CATEGORY_SELECT_ARIA_LABEL)}
                options={subformOptions()}
                value={currentForm()}
                onSelect={(value) => setCurrentForm(value)}
                sameWidth={false}
                hasError={false}
              />
            </Show>
          </div>
          <div class="flex items-center justify-between pb-1 pl-2">
            <Text text={t(($) => $.EXPORT_SUBMISSIONS_COLUMS_TITLE)} as="h3" class="text-header-md text-secondary" />
            <TextButton
              size="medium"
              variant="tertiary"
              onClick={() => toggleAllColumns()}
              text={
                allItemsSelected()
                  ? t(($) => $.EXPORT_SUBMISSIONS_DESELECT_ALL_BUTTON_LABEL)
                  : t(($) => $.EXPORT_SUBMISSIONS_SELECT_ALL_BUTTON_LABEL)
              }
            />
          </div>
          <FieldList
            items={filteredItems()}
            onDragStart={() => setQuery("")}
            onOrderUpdate={(fromIndex, toIndex) => reOrderItem(fromIndex, toIndex, currentForm())}
            class="mb-0.5 h-64"
          />
        </div>
      </Show>

      <Show when={view() === "final"}>
        <div class="mt-4 -mb-px flex h-100 flex-col items-center justify-center border-t border-subtle text-center">
          <div class="flex size-20 flex-none shrink-0 items-center justify-center rounded-full bg-surface-status-subtle-success">
            <Icon name="checkCircle" size="2xl" type="solid" class="icon-status-success" />
          </div>
          <Text
            as="h3"
            text={t(($) => $.EXPORT_SUBMISSIONS_FINAL_TITLE)}
            class="mt-4 mb-1 max-w-85 text-header-lg text-primary"
          />
          <p class="max-w-85 text-medium-regular text-secondary">
            <Translation
              key={keyFromSelector(($) => $.EXPORT_SUBMISSIONS_FINAL_DESCRIPTION)}
              options={{ email: user().emailAddress }}
              components={{
                b: (props) => <span class="text-medium-semibold text-primary">{props.children}</span>,
              }}
            />
          </p>
        </div>
      </Show>

      <div class="flex justify-end gap-1 border-t border-subtle px-4 pt-4">
        <Show when={column() === "custom" && view() === "custom"}>
          <TextButton
            text={t(($) => $.EXPORT_SUBMISSIONS_BACK_BUTTON_LABEL)}
            onClick={() => setView("export")}
            disabled={isExporting()}
            variant="secondary"
            size="medium"
          />
        </Show>
        <Show when={column() === "custom" && view() === "export"}>
          <TextButton
            text={t(($) => $.EXPORT_SUBMISSIONS_NEXT_BUTTON_LABEL)}
            onClick={() => {
              setView("custom");
            }}
            size="medium"
          />
        </Show>
        <Show when={(view() === "export" && column() !== "custom") || view() === "custom"}>
          <TextButton
            text={t(($) => $.EXPORT_SUBMISSIONS_EXPORT_BUTTON_LABEL)}
            onClick={runExport}
            loading={isExporting()}
            size="medium"
          />
        </Show>
        <Show when={view() === "final"}>
          <TextButton
            text={t(($) => $.EXPORT_SUBMISSIONS_DONE_BUTTON_LABEL)}
            onClick={() => setOpen(false)}
            size="medium"
          />
        </Show>
      </div>
    </Popover>
  );
};

export default ExportSubmissionsButton;
